/**
 * SPA-side mirror of the backend's subdomain → org slug extraction.
 *
 * Keep the reserved-subdomain list in sync with `RESERVED_SUBDOMAINS` in
 * `packages/server/src/index.ts` and the `extractSubdomainOrg` /
 * `getSubdomainZone` helpers in `packages/server/src/utils/public-origin.ts`.
 *
 * The zone is declared by the server at runtime (`window.__LOBU_RUNTIME_CONFIG__`)
 * from AUTH_COOKIE_DOMAIN. No zone declared → no subdomain routing, and the app
 * stays on path-based `/{org}` URLs.
 *
 * It is deliberately NOT inferred from `window.location.hostname`. Guessing the
 * zone by stripping the last two labels reads the first label of any ordinary
 * 3-label host as an org slug, so a self-hoster on `service.my-domain.com` was
 * redirected to `{org}.my-domain.com` — a host their reverse proxy did not
 * serve — and the app 404'd (lobu#2183). A build-time flag could not fix that
 * either: Docker self-hosters run a prebuilt frontend and cannot set a Vite var.
 */

import { parseEntityPath } from './url';

const RESERVED_SUBDOMAINS = new Set([
  'www',
  'api',
  'app',
  'admin',
  'auth',
  'mcp',
  'static',
  'assets',
  'cdn',
  'docs',
  'mail',
]);

/** The zone the server declared, or null when routing stays path-based. */
export function getSubdomainZone(): string | null {
  if (typeof window === 'undefined') return null;
  return window.__LOBU_RUNTIME_CONFIG__?.subdomainZone ?? null;
}

export function extractSubdomainOwner(hostname: string, zone: string | null): string | null {
  if (!zone) return null;
  const normalizedHost = hostname.split(':')[0]?.toLowerCase();
  if (!normalizedHost || !normalizedHost.endsWith(`.${zone}`)) return null;

  const sub = normalizedHost.slice(0, -(zone.length + 1));
  if (!sub || sub.includes('.') || RESERVED_SUBDOMAINS.has(sub)) return null;
  return sub;
}

let cached: string | null | undefined;

/** Test-only: clears the owner memoized at application boot. */
export function __resetSubdomainCacheForTests(): void {
  cached = undefined;
}

export function getSubdomainOwner(): string | null {
  if (cached !== undefined) return cached;
  if (typeof window === 'undefined') {
    cached = null;
    return cached;
  }
  cached = extractSubdomainOwner(window.location.hostname, getSubdomainZone());
  return cached;
}

/**
 * The org slug the current URL addresses — the subdomain owner when on a
 * per-org subdomain (`{org}.{zone}`), otherwise the first path segment on
 * path-based hosts. Returns null when neither applies: bare origin, a
 * reserved or user-space (`@user`) first segment, or no `window`.
 *
 * This is the URL→org authority for non-hook code: `getPathScope` (API calls)
 * and the invalidation SSE derive here. `useOrgContext` applies the same
 * precedence (subdomain wins, then path) off the router location, which is
 * equivalent in production because the two pathname sources only differ on
 * per-org subdomains — where the subdomain branch already wins. Keeping one
 * precedence everywhere means a future URL-format change can never make the
 * derivations disagree and silently send API calls to `/api/<wrong-slug>/...`.
 */
export function resolveUrlOrgSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const subdomainOwner = getSubdomainOwner();
  if (subdomainOwner) return subdomainOwner;
  // Read the bare `location` global (the URL bar). On per-org subdomains the
  // router pathname is owner-prefixed while the URL bar is stripped, but the
  // subdomain branch above already wins there; on path-based hosts the router
  // pathname and the URL bar are the same.
  const parsed = parseEntityPath(location.pathname);
  if (parsed.owner && !parsed.ownerInfo?.isUser) return parsed.owner;
  return null;
}

/**
 * Builds the URL for an org's workspace home given the current host.
 * - With a configured zone, returns the target's absolute org URL unless the
 *   browser is already on that org's subdomain.
 * - Without a configured zone, returns the path-based in-app URL.
 */
export function buildOwnerHref(targetSlug: string): { kind: 'spa'; to: string } | { kind: 'cross-host'; href: string } {
  const zone = getSubdomainZone();
  if (!zone) {
    return { kind: 'spa', to: `/${targetSlug}` };
  }
  const currentSubdomain = getSubdomainOwner();
  if (currentSubdomain === targetSlug) {
    return { kind: 'spa', to: '/' };
  }
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return { kind: 'cross-host', href: `${protocol}//${targetSlug}.${zone}/` };
}
